import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import {
  handleClearFields,
  handleUpdateFields,
  getCashbackGamesItem,
  createCashbackGames,
  updateCashbackGames,
  deleteCashbackGamesItem,
  removeCashbackGames,
} from "store/liveOps/cashbackGames/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { ModalCasbackGamesTabs } from "configs/types";
import { GameItem } from "store/gameCenter/games/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { wagersListLimits } from "configs/limits";

import Button from "components/buttons/Button";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import SearchInput from "components/inputs/SearchInput/SearchInput";
import Pagination from "components/Pagination";
import FilterTabs from "components/FilterTabs";

import { Modal } from "components/Modal/Modal";
import noImage from "assets/images/image_not_found.webp";
import styles from "./LiveOpsCashbackGamesEditModal.module.scss";
import { Options } from "components/table/components/Options/Options";
import ModalSkeleton from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import CopyToClipboard from "components/common/CopyToClipboard/CopyToClipboard";

const LiveOpsCashbackGamesEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [tabs] = useState<ModalCasbackGamesTabs[]>([
    ModalCasbackGamesTabs.main,
    ModalCasbackGamesTabs.games,
    // ModalCasbackGamesTabs.settings,
  ]);
  const [activeTab, setActiveTab] = useState<ModalCasbackGamesTabs>(
    ModalCasbackGamesTabs.main,
  );
  const [deleteValue, setDeleteValue] = useState<string>("");
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] =
    useState<string>("");
  const [activePage, setActivePage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [gamesList, setGamesList] = useState<GameItem[]>([]);
  const [currentLimit, setLimit] = useState<number>(wagersListLimits[0]);
  const [isGamesLoading, setIsGamesLoading] = useState(false);

  const isItemFetched = useAppSelector(
    (store) => store["cashback-games"].isItemFetched,
  );
  const isItemFetching = useAppSelector(
    (store) => store["cashback-games"].isItemFetching,
  );
  const payload = useAppSelector((store) => store["cashback-games"].payload);
  const formErrors = useFormErrors();
  const permissions = usePermissionHelper();
  const canSave = searchParams.get("entityId")
    ? Boolean(permissions?.update)
    : Boolean(permissions?.create);

  const handleLoadGames = async () => {
    setIsGamesLoading(true);

    try {
      const result = await instance.get(
        `${apiRoutes.cashbacksGamesListGamesAll}/${searchParams.get("entityId")}?limit=${currentLimit}&offset=${activePage - 1}&gameTitle=${encodeURIComponent(debouncedSearchValue)}`,
      );

      setGamesList(result.data.items);
      setTotalItems(result.data.totalItems);
      setTotalPages(result.data.totalPages);
    } catch {
      console.log("handleLoadGames error");
    } finally {
      setIsGamesLoading(false);
    }
  };

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setActivePage(1);
      setDebouncedSearchValue(searchValue.trim());
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [searchValue]);

  useEffect(() => {
    handleLoadGames();
  }, [debouncedSearchValue, activePage, currentLimit]);

  useEffect(() => {
    if (searchParams.get("entityId") !== null) {
      if (searchParams.get("tab") !== null) {
        setActiveTab(searchParams.get("tab") as ModalCasbackGamesTabs);
      }
      dispatch(
        getCashbackGamesItem({
          id: searchParams.get("entityId") ?? "",
        }),
      );
      setIsEdit(true);
    } else {
      setIsEdit(false);
    }
  }, [searchParams, isItemFetched]);

  const isLoaded = () => {
    if (searchParams.get("entityId") !== null && !isItemFetched) {
      return false;
    }

    return true;
  };

  const loaded = isLoaded();

  const handleData = (key: string, value: string | boolean | number) => {
    // @ts-ignore
    dispatch(handleUpdateFields({ [key]: value }));
  };

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const handleApply = async () => {
    if (!canSave) {
      return;
    }

    if (!payload) return;

    const isEditMode = searchParams.get("entityId") !== null;

    formErrors.resetErrors();

    try {
      if (isEditMode) {
        await dispatch(updateCashbackGames(payload)).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } else {
        const result = await dispatch(createCashbackGames(payload)).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );

        navigate(
          `${location.pathname}?activeModal=cashbackGames&entityId=${result}`,
        );
      }
    } catch (error) {
      handleApiError({
        error,
        t,
        dispatch,
        formErrors,
      });
    }
  };

  const onSearch = (e: string) => {
    setSearchValue(e);
  };

  const handlePrevClick = () => {
    if (activePage > 1) {
      setActivePage(activePage - 1);
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      setActivePage(activePage + 1);
    }
  };

  const handleGetPayload = () => {
    return payload?.title;
  };

  const handleDeleteClick = async () => {
    const result = await dispatch(deleteCashbackGamesItem(payload?.id ?? 0));
    if (result.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
    handleCloseClick();
  };

  const handleDeleteGame = async (gameId: number) => {
    await dispatch(
      removeCashbackGames({
        cashbackId: Number(searchParams.get("entityId")),
        gameIds: [gameId],
      }),
    );
    handleLoadGames();
    dispatch(
      showNotification({
        title: t("success"),
        message: t("entityDeleteSuccess"),
        type: NotificationTypes.success,
      }),
    );
  };

  const renderTab = () => {
    if (activeTab === ModalCasbackGamesTabs.main) {
      return (
        <div className="cashback-games-form-wrapper">
          <div className={`loyalty-points-content ${styles.mainForm}`}>
            <div className="loyalty-points-form-box--item">
              <InnerInput
                value={payload?.title ?? ""}
                placeholder={t("title")}
                label={t("title")}
                type="text"
                id="cashback-games-title"
                onChange={(e) => {
                  handleData("title", e);
                  formErrors.clearFieldError("title");
                }}
                hasError={Boolean(formErrors.errors["title"])}
                disabled={!canSave}
              />
            </div>
            <div className="loyalty-points-form-box--item">
              <FilterTabs
                value={payload?.cashBackType}
                isInner
                label={t("type")}
                onSelect={(e) => {
                  handleData("cashBackType", e);
                  formErrors.clearFieldError("cashBackType");
                }}
                hasError={!!formErrors.errors.cashBackType}
                disabled={!canSave}
                options={[
                  {
                    value: "hourly",
                    label: t("hourly"),
                  },
                  {
                    value: "daily",
                    label: t("daily"),
                  },
                  {
                    value: "weekly",
                    label: t("weekly"),
                  },
                  {
                    value: "monthly",
                    label: t("monthly"),
                  },
                  {
                    value: "yearly",
                    label: t("yearly"),
                  },
                ]}
              />
            </div>
            <div className="loyalty-points-form-box--item">
              <InnerInput
                maxCodePoints={2500}
                value={payload?.description ?? ""}
                placeholder={t("description")}
                label={t("description")}
                type="textarea"
                id="cashback-games-description"
                onChange={(e) => {
                  handleData("description", e);
                  formErrors.clearFieldError("description");
                }}
                hasError={Boolean(formErrors.errors["description"])}
                disabled={!canSave}
              />
            </div>
          </div>
        </div>
      );
    }
    if (activeTab === ModalCasbackGamesTabs.games) {
      return (
        <div className={`wager-list-games ${styles.gamesTab}`}>
          <div className="games-order-search">
            <SearchInput
              placeholder={t("searchGame")}
              value={searchValue}
              onChange={onSearch}
              isSmall
            />
          </div>

          <div className={`games-order-list ${styles.gamesTable}`}>
            <div className="item">
              <div className={styles.gameIdColumn}>ID</div>
              <div className={styles.gameImageColumn}>{t("image")}</div>
              <div className={styles.gameTitleColumn}>{t("game")}</div>
              <div className={styles.gameProviderColumn}>{t("provider")}</div>
              {permissions?.update && (
                <div className={styles.gameOptionsColumn}>{t("options")}</div>
              )}
            </div>

            {isGamesLoading ? (
              <ModalTabSkeleton />
            ) : (
              gamesList.map((el) => (
                <div className="item" key={el.id}>
                  <div className={styles.gameIdColumn}>{el.id}</div>

                  <div className={styles.gameImageColumn}>
                    <img
                      src={(el.image as string) || noImage}
                      alt=""
                      className="game-img"
                      onError={(event) => {
                        event.currentTarget.src = noImage;
                        event.currentTarget.onerror = null;
                      }}
                    />
                  </div>

                  <div className={styles.gameTitleColumn}>
                    <CopyToClipboard text={el.title} />
                    <span>{el.title}</span>
                  </div>

                  <div className={styles.gameProviderColumn}>
                    {el.provider.title}
                  </div>
                  
                  {permissions?.update && (
                    <div className={styles.gameOptionsColumn}>
                      <Options
                        id={el.id}
                        hasDelete
                        hasEdit={false}
                        instantDelete
                        onDelete={() => handleDeleteGame(el.id)}
                      />
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <Pagination
            handleLimitClick={(e) => {
              setLimit(e);
              setActivePage(1);
            }}
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            limitsArray={wagersListLimits}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
          />
        </div>
      );
    }
    if (activeTab === ModalCasbackGamesTabs.settings) {
      return (
        <div className="game-form-wrapper">
          <div className="game-form-content delete">
            <div className="game-form-box">
              <div className="game-form-box--item">
                <InnerInput
                  value={deleteValue}
                  placeholder={t("enterEntityTitleToDelete")}
                  label={t("areYouSureYouWantDeleteThisEntity")}
                  type="text"
                  id="game-title"
                  onChange={(e) => setDeleteValue(e)}
                />
                <Button
                  isDelete
                  txt={t("delete")}
                  handleClick={handleDeleteClick}
                  disabled={deleteValue !== handleGetPayload()}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }
    return <div className="game-form-wrapper"></div>;
  };

  const handleTabClick = (tab: ModalCasbackGamesTabs) => {
    if (!isEdit && tab !== ModalCasbackGamesTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setActiveTab(tab);
  };

  return (
    <Modal onClose={handleCloseClick} size="sm">
      <Modal.Header>{t("cashbackGame")}</Modal.Header>

      {!loaded ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            canBeClicked={isEdit}
            onClick={handleTabClick}
          />

          <Modal.Content
            className={
              activeTab === ModalCasbackGamesTabs.games
                ? styles.gamesContent
                : undefined
            }
          >
            {renderTab()}
          </Modal.Content>

          {canSave &&
            activeTab !== ModalCasbackGamesTabs.games && (
              <Modal.Footer>
                <Button
                  isSecondary
                  txt={t("cancel")}
                  handleClick={handleCloseClick}
                />
                <Button
                  isPrimary
                  isLoad={isItemFetching}
                  handleClick={handleApply}
                  txt={t("save")}
                />
              </Modal.Footer>
            )}
        </>
      )}
    </Modal>
  );
};

export default LiveOpsCashbackGamesEditModal;
